import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { IconRegularCopy } from '@notino/react-styleguide';

import { PortalTooltip } from '@components/PortalTooltip';

import { messages } from '../../messages';
import { Divider } from '../../styled';
import { useHasStressOnVoucherDiscount } from '../../useHasStressOnVoucherDiscount';
import {
  TextWrapper,
  CodeWrapper,
  DiscountAmountWrapper,
  DiscountInfoDescription,
} from '../styled';

import { StyledInteractiveArea } from './styled';
import { VoucherTerms } from './Terms';

export interface IProductDiscountProps {
  highlightedDiscountAmount: string | JSX.Element;
  instructionsText: JSX.Element;
  infoTooltipText?: JSX.Element;
  discountCode: string;
  highlightedDiscountAmountColor?: string;
  hasDivider?: boolean;
  middleSection?: React.ReactElement;
}

export const ProductDiscount: React.FC<
  IProductDiscountProps & {
    columnOnMobile: boolean;
  }
> = ({
  highlightedDiscountAmount,
  discountCode,
  instructionsText,
  infoTooltipText,
  highlightedDiscountAmountColor,
  hasDivider,
  middleSection,
  columnOnMobile,
}) => {
  const [showIsCopied, setShowIsCopied] = React.useState(false);
  const tooltipTimeout = React.useRef<NodeJS.Timeout | null>(null);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(discountCode);
    setShowIsCopied(true);
    if (tooltipTimeout.current) {
      clearTimeout(tooltipTimeout.current);
    }
    tooltipTimeout.current = setTimeout(() => setShowIsCopied(false), 2000);
  };

  const isStressed = useHasStressOnVoucherDiscount();

  return (
    <DiscountInfoDescription>
      <TextWrapper isStressed={isStressed} columnOnMobile={columnOnMobile}>
        <DiscountAmountWrapper
          isStressed={isStressed}
          color={highlightedDiscountAmountColor}
        >
          {highlightedDiscountAmount}
        </DiscountAmountWrapper>

        <span>
          <FormattedMessage
            {...messages.withCode}
            values={{
              code: <CodeWrapper>{discountCode}</CodeWrapper>,
            }}
          />

          <PortalTooltip
            isOpen={showIsCopied ? true : undefined}
            content={
              <PortalTooltip.Content shouldWrapText={false}>
                {showIsCopied ? (
                  <FormattedMessage {...messages.codeCopied} />
                ) : (
                  <FormattedMessage {...messages.copyCode} />
                )}
              </PortalTooltip.Content>
            }
          >
            <StyledInteractiveArea onClick={handleCopy}>
              <IconRegularCopy height="20px" color="icon.tertiary" />
            </StyledInteractiveArea>
          </PortalTooltip>
        </span>
      </TextWrapper>

      {hasDivider && <Divider />}

      {middleSection}

      <VoucherTerms
        infoTooltipText={infoTooltipText}
        instructionsText={instructionsText}
      />
    </DiscountInfoDescription>
  );
};
